import { useState, useEffect } from 'react';
import { getUserStats, getGlobalStats } from '../api/client';

interface UserStatsData {
  totalUsers: number;
  authenticatedUsers: number;
  anonymousUsers: number;
  activeToday: number;
  activeThisWeek: number;
}

export default function Users() {
  const [stats, setStats] = useState<UserStatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchData() {
      try {
        // Try the dedicated user stats endpoint first
        let userStatsData = null;
        try {
          const result = await getUserStats();
          userStatsData = result.data;
        } catch {
          // Fall back to global stats
        }

        if (userStatsData) {
          setStats({
            totalUsers: userStatsData.totalUsers,
            authenticatedUsers: userStatsData.authenticatedUsers,
            anonymousUsers: userStatsData.anonymousUsers,
            activeToday: userStatsData.activeToday,
            activeThisWeek: userStatsData.activeThisWeek,
          });
        } else {
          // Fallback: use global stats
          const globalResult = await getGlobalStats();
          setStats({
            totalUsers: globalResult.data?.totalUsers || 0,
            authenticatedUsers: 0,
            anonymousUsers: globalResult.data?.totalUsers || 0,
            activeToday: 0,
            activeThisWeek: 0,
          });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load user data');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  return (
    <div>
      {/* Page Header */}
      <div className="flex justify-between items-start mb-8 pb-5 border-b-2 border-gray-200">
        <div>
          <h2 className="text-3xl font-bold text-slate-800 mb-1">Users</h2>
          <div className="text-gray-500">User management and analytics</div>
        </div>
        <div className="flex gap-2">
          <button className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors text-sm">
            Export Users
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-500 p-4 rounded-lg mb-6">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center p-12 text-gray-500">
          Loading users...
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-5 mb-8">
            <div className="bg-white p-5 rounded-lg shadow">
              <div className="flex justify-between items-start mb-2">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-xl">
                  &#x1F465;
                </div>
              </div>
              <div className="text-3xl font-bold text-slate-800 mb-0.5">
                {stats?.totalUsers?.toLocaleString() || 0}
              </div>
              <div className="text-gray-500 text-sm">Total Users</div>
            </div>

            <div className="bg-white p-5 rounded-lg shadow">
              <div className="flex justify-between items-start mb-2">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center text-xl">
                  &#x1F512;
                </div>
              </div>
              <div className="text-3xl font-bold text-slate-800 mb-0.5">
                {stats?.authenticatedUsers || 0}
              </div>
              <div className="text-gray-500 text-sm">Authenticated</div>
            </div>

            <div className="bg-white p-5 rounded-lg shadow">
              <div className="flex justify-between items-start mb-2">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-xl">
                  &#x1F464;
                </div>
              </div>
              <div className="text-3xl font-bold text-slate-800 mb-0.5">
                {stats?.anonymousUsers || 0}
              </div>
              <div className="text-gray-500 text-sm">Anonymous</div>
            </div>

            <div className="bg-white p-5 rounded-lg shadow">
              <div className="flex justify-between items-start mb-2">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center text-xl">
                  &#x1F4C8;
                </div>
                <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-800">Today</span>
              </div>
              <div className="text-3xl font-bold text-slate-800 mb-0.5">
                {stats?.activeToday || 0}
              </div>
              <div className="text-gray-500 text-sm">Active Today</div>
            </div>
          </div>

          {/* User Breakdown */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="bg-slate-700 text-white px-5 py-4">
                <span className="font-bold">&#x1F4CA; User Types</span>
              </div>
              <div className="p-5">
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-slate-800">Authenticated (Apple ID)</span>
                      <span className="text-sm text-gray-500">{stats?.authenticatedUsers || 0}</span>
                    </div>
                    <div className="h-3 bg-gray-200 rounded overflow-hidden">
                      <div
                        className="h-full bg-green-500"
                        style={{ width: `${((stats?.authenticatedUsers || 0) / (stats?.totalUsers || 1)) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-slate-800">Anonymous Users</span>
                      <span className="text-sm text-gray-500">{stats?.anonymousUsers || 0}</span>
                    </div>
                    <div className="h-3 bg-gray-200 rounded overflow-hidden">
                      <div
                        className="h-full bg-gray-500"
                        style={{ width: `${((stats?.anonymousUsers || 0) / (stats?.totalUsers || 1)) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="bg-slate-700 text-white px-5 py-4">
                <span className="font-bold">&#x1F4C5; User Activity</span>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-slate-800">{stats?.activeToday || 0}</div>
                    <div className="text-sm text-gray-500">Active Today</div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-slate-800">{stats?.activeThisWeek || 0}</div>
                    <div className="text-sm text-gray-500">Active This Week</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-slate-700 text-white px-5 py-4">
              <span className="font-bold">&#x2139;&#xFE0F; About User Data</span>
            </div>
            <div className="p-5">
              <p className="text-gray-600 leading-relaxed">
                User data is collected with privacy in mind. Anonymous users are tracked via device-specific identifiers stored in the iOS Keychain.
                Authenticated users sign in with Apple ID, and only their Apple-provided user ID is stored - no email or personal information is retained.
                All user statistics are aggregated and individual user activity is not exposed.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
